import { apiClient } from '../lib/api';
import type {
  Comment,
  CommentPage,
  CommentTargetType,
  CreateCommentRequest,
  EditCommentRequest,
  ReactionRequest,
} from '../types/comments';

// Backend list envelope:
//   { status: 'success', data: { data: Comment[], next_before, next_after } }
// We project that into a FE-internal CommentPage where `next_cursor` carries
// whichever direction the caller is paginating in (descending → next_before;
// ascending → next_after).
interface ListPageRaw {
  data: Comment[];
  next_before: string | null;
  next_after: string | null;
}

interface ListEnvelope {
  status: 'success';
  data: ListPageRaw;
}

interface SingleEnvelope {
  status: 'success';
  data: { comment: Comment };
}

export interface ListCommentsParams {
  before?: string;
  after?: string;
  limit?: number;
}

function toPage(raw: ListPageRaw, direction: 'before' | 'after'): CommentPage {
  return {
    comments: raw.data ?? [],
    next_cursor: direction === 'before' ? raw.next_before : raw.next_after,
  };
}

export async function listComments(
  targetType: CommentTargetType,
  targetId: string,
  params: ListCommentsParams = {}
): Promise<CommentPage> {
  const search = new URLSearchParams({ target_type: targetType, target_id: targetId });
  if (params.before) search.set('before', params.before);
  if (params.after) search.set('after', params.after);
  if (params.limit != null) search.set('limit', String(params.limit));
  const res = await apiClient.get<ListEnvelope>(`/comments?${search.toString()}`);
  return toPage(res.data, params.after ? 'after' : 'before');
}

export async function listReplies(
  commentId: string,
  params: ListCommentsParams = {}
): Promise<CommentPage> {
  const search = new URLSearchParams();
  if (params.before) search.set('before', params.before);
  if (params.after) search.set('after', params.after);
  if (params.limit != null) search.set('limit', String(params.limit));
  const qs = search.toString();
  const res = await apiClient.get<ListEnvelope>(
    `/comments/${commentId}/replies${qs ? `?${qs}` : ''}`
  );
  return toPage(res.data, params.before ? 'before' : 'after');
}

export async function getComment(commentId: string): Promise<Comment> {
  const res = await apiClient.get<SingleEnvelope>(`/comments/${commentId}`);
  return res.data.comment;
}

export async function createComment(req: CreateCommentRequest): Promise<Comment> {
  const res = await apiClient.post<SingleEnvelope>(`/comments`, req);
  return res.data.comment;
}

export async function editComment(
  commentId: string,
  req: EditCommentRequest
): Promise<Comment> {
  const res = await apiClient.put<SingleEnvelope>(`/comments/${commentId}`, req);
  return res.data.comment;
}

export async function deleteComment(commentId: string): Promise<void> {
  await apiClient.delete(`/comments/${commentId}`);
}

export async function addReaction(
  commentId: string,
  req: ReactionRequest
): Promise<Comment> {
  const res = await apiClient.post<SingleEnvelope>(`/comments/${commentId}/reactions`, req);
  return res.data.comment;
}

export async function removeReaction(
  commentId: string,
  req: ReactionRequest
): Promise<Comment> {
  const res = await apiClient.delete<SingleEnvelope>(`/comments/${commentId}/reactions`, {
    data: req,
  });
  return res.data.comment;
}
